import { applySeo } from "@/lib/seo-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/site/Layout";
import { OfficeMap } from "@/components/site/OfficeMap";
import { TeamSection } from "@/components/site/TeamSection";
import { CasesSection } from "@/components/site/CasesSection";
import { useServicesList } from "@/lib/services";

import { listProductsPublic, getPublicSettings } from "@/lib/content.functions";
import lawyerHeroMobile from "@/assets/charlene-portrait-mobile.webp";
import lawyerHero from "@/assets/charlene-portrait.webp";
import lawyerHero2x from "@/assets/charlene-portrait@2x.webp";
import aboutImage from "@/assets/charlene-portrait-2.webp";
import heroVideoAsset from "@/assets/hero-legal-bg.mp4.asset.json";
import heroPosterAsset from "@/assets/hero-poster.webp.asset.json";
import ogLogo from "@/assets/logo-rrlex-mark.webp";

import officeHero from "@/assets/hero-office.webp";
const ogImageUrl = `https://rrlexjuristas.com${ogLogo}`;
const heroVideoSrc = heroVideoAsset.url;
const heroPoster = heroPosterAsset.url;
const heroPortraitSrcSet = `${lawyerHeroMobile} 500w, ${lawyerHero} 600w, ${lawyerHero2x} 1200w`;
const heroPortraitSizes = "(max-width: 768px) 92vw, (max-width: 1280px) 45vw, 560px";
import { ArrowRight, Phone, Scale, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";

function formatPrice(cents: number, currency: string, lang: string) {
  const locale = lang === "pt" ? "pt-BR" : lang === "en" ? "en-GB" : "es-ES";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export const Route = createFileRoute("/")({
  component: Home,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Abogada Penalista en Sevilla — RR Lex Juristas" },
      {
        name: "description",
        content:
          "Despacho de abogadas en Sevilla y Camas. Defensa penal, familia y mercantil liderado por Charlene Ramos Rodrigues, penalista de referencia.",
      },
      { property: "og:title", content: "Abogada Penalista en Sevilla — RR Lex Juristas" },
      {
        property: "og:description",
        content:
          "Despacho de abogadas en Sevilla y Camas. Defensa penal, familia y mercantil liderado por Charlene Ramos Rodrigues, penalista de referencia.",
      },
      { property: "og:url", content: "https://rrlexjuristas.com/" },
      {
        property: "og:image",
        content: ogImageUrl,
      },
    ],
    links: [
      { rel: "canonical", href: "https://rrlexjuristas.com/" },
      { rel: "preload", as: "image", href: heroPoster, fetchPriority: "high" } as never,
      {
        rel: "preload",
        as: "image",
        href: lawyerHero,
        imageSrcSet: heroPortraitSrcSet,
        imageSizes: heroPortraitSizes,
        fetchPriority: "high",
      } as never,


    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          name: "RR Lex Juristas",
          url: "https://rrlexjuristas.com",
          telephone: "+34665649354",
          email: "rrlexjuristas@icasevilla.org",
          image: ogImageUrl,
          address: [
            {
              "@type": "PostalAddress",
              streetAddress: "C/ Almutamid, 6 — Edificio Vega 1",
              addressLocality: "Camas",
              addressRegion: "Sevilla",
              postalCode: "41900",
              addressCountry: "ES",
            },
            {
              "@type": "PostalAddress",
              streetAddress: "Calle Sierpes 48, 2-H",
              addressLocality: "Sevilla",
              postalCode: "41004",
              addressCountry: "ES",
            },
          ],
          areaServed: "ES",
        }),
      },
    ],
  }))(ctx)),
});

function CountUp({ end, prefix = "", duration = 2000 }: { end: number; prefix?: string; duration?: number }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const startTime = performance.now();
            const tick = (now: number) => {
              const progress = Math.min((now - startTime) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(Math.round(end * eased));
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {value.toLocaleString()}
    </span>
  );
}

function Home() {
  const { tk, lang } = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const services = useServicesList();

  const heroVideo = useMedia("home.hero.video", heroVideoSrc, "");
  const heroPortrait = useMedia("home.hero.portrait", lawyerHero, "Charlene Ramos Rodrigues, abogada penalista");


  const aboutPortrait = useMedia("home.about.portrait", aboutImage, "Despacho de abogadas RR Lex Juristas en Sevilla");
  const officeBg = useMedia("home.office.hero", officeHero, "Despacho RR Lex Juristas");
  const list = useServerFn(listProductsPublic);
  const getSettings = useServerFn(getPublicSettings);
  const { data, isLoading } = useQuery({
    queryKey: ["products-public"],
    queryFn: () => list(),
  });
  const settingsQuery = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => getSettings(),
  });
  const products = data?.products ?? [];
  const consultationEnabled = settingsQuery.data?.settings.consultation_payment_enabled ?? true;
  const consultationPriceCents = settingsQuery.data?.settings.consultation_price_cents ?? 9900;

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // iOS Safari requires the video element to exist on first render with
  // muted + playsInline + autoplay to allow programmatic playback. Some
  // iOS versions still need an explicit .play() call after mount.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);



  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gold/15">
        <img
          src={heroPoster}
          alt=""
          aria-hidden
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-left md:object-center opacity-50 transition-opacity duration-500"
          style={{ opacity: isVideoPlaying ? 0 : 0.5 }}
        />
        <video
          ref={videoRef}
          src={heroVideo.url}
          preload="auto"
          autoPlay
          muted
          loop
          playsInline
          {...({ "webkit-playsinline": "true" } as Record<string, string>)}
          aria-hidden
          onPlaying={() => setIsVideoPlaying(true)}
          className="absolute inset-0 w-full h-full object-cover object-left md:object-center opacity-50"
        />

        <div
          className="absolute inset-0 bg-gradient-to-br from-background/60 via-background/40 to-transparent"
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-8 md:py-0 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <span className="gold-rule" />
              <span className="text-xs tracking-[0.4em] uppercase text-gold">
                {tk("home.b2cba9", "Abogada Penalista")}
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] text-foreground">
              {tk("home.91bcaf", "Abogada Penalista en ")}
              <em className="not-italic gold-gradient">{tk("home.4dbbe3", "Sevilla")}</em>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {tk("home.37aeed", "Charlene Ramos Rodrigues está considerada como una de las mejores abogadas penalistas de España. Un despacho moderno, humano y comprometido con la justicia.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 rounded-full bg-gold text-primary-foreground px-7 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
              >
                {tk("home.a5e0e5", "Reservar consulta")}{" "}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${tk("home.phone.tel", "+34665649354")}`}
                className="inline-flex items-center gap-3 rounded-full border border-gold/40 text-foreground px-7 py-3.5 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
              >
                <Phone size={14} /> {tk("home.phone.display", "665 649 354")}
              </a>
            </div>
          </div>

          <div className="relative">
            <img
              src={heroPortrait.url}
              srcSet={heroPortrait.url === lawyerHero ? heroPortraitSrcSet : undefined}
              sizes={heroPortrait.url === lawyerHero ? heroPortraitSizes : undefined}
              alt={heroPortrait.alt}
              width={600}
              height={742}
              fetchPriority="high"
              decoding="async"
              className="relative w-full h-[520px] md:h-[640px] object-cover rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-3 gap-6 text-center">
          {[
            { n: 3000, prefix: "+", l: tk("home.ac2701", "Casos Resueltos") },
            { n: 20, prefix: "+", l: tk("home.b1a40b", "Casos Famosos") },
            { n: 360, prefix: "", l: tk("home.2b7c37", "Días al Año") },
          ].map((s) => (
            <div key={s.l} className="px-2">
              <div className="font-serif text-4xl md:text-6xl gold-gradient">
                <CountUp end={s.n} prefix={s.prefix} />
              </div>
              <div className="mt-3 text-[10px] md:text-xs tracking-[0.35em] uppercase text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PACKAGES */}
      <section className="border-b border-gold/15 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.05f794", "Paquetes de servicios")}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">
              {tk("home.2c16b9", "Trámites cerrados, ")}
              <em className="not-italic gold-gradient">{tk("home.0bcb23", "precio único")}</em>
            </h2>
            <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("home.d735af", "Servicios de tramitación con alcance definido y precio fijo. Sin sorpresas y con seguimiento personalizado hasta el final del expediente.")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {consultationEnabled && (
              <div className="rounded-2xl border border-gold/30 bg-card/30 p-8 flex flex-col">
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("home.4feb51", "Consulta")}</div>
                <h3 className="font-serif text-3xl mt-3">
                  {tk("home.d933a3", "Consulta jurídica de 45 min")}
                </h3>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">
                  {tk("home.e9d315", "Sesión personalizada para analizar tu caso, resolver dudas y orientarte sobre los próximos pasos. Confirmación inmediata por email.")}
                </p>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
                >
                  {tk("home.900e6d", "Reservar")} <ArrowRight size={12} />
                </Link>
              </div>
            )}
            {isLoading && (
              <div className="col-span-full flex justify-center py-12 text-muted-foreground">
                <Loader2 className="animate-spin" />
              </div>
            )}
            {!isLoading && products.length === 0 && !consultationEnabled && (
              <p className="col-span-full text-center text-muted-foreground py-12">
                {tk("home.a55c9a", "No hay paquetes disponibles por el momento.")}
              </p>
            )}
            {products.map((p) => (
              <div key={p.slug} className="rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col">
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("home.cf08aa", "Paquete")}</div>
                <h3 className="font-serif text-3xl mt-3">{tk(`products.${p.slug}.name`, p.name)}</h3>
                {!p.priceless && (
                  <div className="text-3xl gold-gradient font-serif mt-4">
                    {formatPrice(p.price_cents, p.currency, lang)}
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">{tk(`products.${p.slug}.description`, p.description)}</p>
                <Link
                  to="/products/$slug"
                  params={{ slug: p.slug }}
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
                >
                  {tk("home.b73399", "Contratar")} <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="text-xs tracking-[0.3em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold inline-flex items-center gap-2"
            >
              {tk("home.a5444a", "Ver todos los paquetes")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-5 gap-12 items-center">
        <div className="md:col-span-2 relative">
          <img
            src={aboutPortrait.url}
            alt={aboutPortrait.alt}
            width={1600}
            height={1000}
            loading="lazy"
            className="w-full h-[480px] object-cover rounded-xl"
          />
          <div className="absolute -bottom-6 -right-6 hidden md:block rounded-xl border border-gold/40 p-6 bg-background w-48">
            <div className="font-serif text-3xl text-gold">25+</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-1">
              {tk("home.a69b6f", "Años de práctica")}
            </div>
          </div>
        </div>
        <div className="md:col-span-3">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("home.98caff", "Conócenos")}</span>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">RR Lex Juristas</h2>
          <div className="gold-divider my-6 max-w-[120px]" />
          <p className="text-muted-foreground leading-relaxed">
            {tk("home.31d087", "Abogacía moderna, accesible y centrada en las personas. Nuestra misión es acompañar, defender y asesorar a quienes acuden a nosotras, ofreciendo un servicio jurídico de calidad, transparente y humano.")}
          </p>
          <p className="text-muted-foreground leading-relaxed mt-4">
            {tk("home.dc15bc", "Nos mueve la vocación por la justicia social, el respeto a la dignidad de cada cliente y la búsqueda de soluciones eficaces, tanto en sede judicial como a través de la mediación.")}
          </p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold"
          >
            {tk("home.98caff", "Conócenos")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* TEAM */}
      <TeamSection />

      {/* CASES */}
      <CasesSection />


      {/* VALUES */}
      <section className="border-y border-gold/15 bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
          {[
            {
              i: Scale,
              t: tk("home.c3fb4f", "Justicia"),
              d: tk("home.41d131", "Compromiso con las personas, no con los conflictos."),
            },
            {
              i: ShieldCheck,
              t: tk("home.ebb5ec", "Transparencia"),
              d: tk("home.69c809", "Honorarios claros y trato honesto en todo momento."),
            },
            {
              i: Sparkles,
              t: tk("home.f1cd0f", "Innovación"),
              d: tk("home.461d5d", "Métodos alternativos como la mediación jurídica."),
            },
          ].map(({ i: Icon, t: title, d }) => (
            <div key={title} className="rounded-2xl border border-gold/20 p-8 hover:border-gold transition-colors">
              <Icon className="text-gold" size={28} strokeWidth={1.2} />
              <h3 className="mt-6 font-serif text-2xl">{title}</h3>
              <div className="gold-divider my-4 max-w-[60px]" />
              <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.7d4455", "Áreas de Especialización")}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-2xl">
              {tk("home.a626c5", "Especialistas en cada rama del Derecho")}
            </h2>
          </div>
          <Link
            to="/services"
            className="text-xs tracking-[0.3em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold inline-flex items-center gap-2 self-start"
          >
            {tk("home.3efc40", "Ver todas")} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.slice(0, 9).map((s) => (
            <div
              key={s.slug}
              className="group rounded-2xl border border-gold/15 bg-card/30 p-8 hover:border-gold/60 hover:bg-card/60 transition-colors"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("home.abb84c", "Área")}</div>
              <h3 className="font-serif text-2xl mt-2 group-hover:text-gold transition-colors">{s.name}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-gold/20 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url(${officeBg.url})`, backgroundSize: "cover", backgroundPosition: "center" }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30"
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-6 py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("home.8454ad", "¿Necesita ayuda legal?")}
          </span>
          <h2 className="font-serif text-4xl md:text-6xl mt-5 max-w-3xl mx-auto">
            {tk("home.d8ebc4", "Pida cita con nosotras o consulte ")}
            <em className="not-italic gold-gradient">online</em>
            {tk("home.bb71a3", " sin compromiso.")}
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="rounded-full bg-gold text-primary-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
            >
              {tk("home.2cd1c2", "Contactar")}
            </Link>
            <a
              href="mailto:rrlexjuristas@icasevilla.org"
              className="rounded-full border border-gold/40 px-8 py-4 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
            >
              {tk("home.36a3d5", "Escribir un email")}
            </a>
          </div>
        </div>
      </section>
      <OfficeMap />
    </Layout>
  );
}
